/**
 * client/src/audio/AudioEngine.ts — the transport. Two `<audio>` elements, ping-ponged.
 *
 * This is where P2's gate is won or lost: press play, the sentence lights up slightly ahead of the
 * voice, the page turns itself, **lock the screen and it keeps reading through at least four page
 * boundaries**, the AirPods squeeze pauses it, and changing the font size mid-sentence does not seek
 * or gap the audio. Everything except the highlight lives in this file.
 *
 * ── Web Audio is rejected, and this is not a preference ─────────────────────────────────────────
 * **iOS suspends an `AudioContext` when the page is backgrounded or the screen locks.** A scheduling
 * graph is the only way to get truly gapless playback of many short clips, and it cannot play with
 * the screen off — which is the stated use case, not an edge case. `<audio>` also respects the silent
 * switch, and a book that plays out loud in a meeting is a betrayal you only discover once.
 * Gaplessness is bought instead by §4.2's pacing: 620–900 ms of `pageHead` silence at the head of
 * every page, so the element swap happens inside a pause we put there deliberately and even a 200 ms
 * Safari `src` swap is inaudible. **Do not "upgrade" this to Web Audio.** It would test perfectly on
 * a desktop and fail on the only device that matters, at the only moment that matters.
 *
 * ── The three ways a page ends, and why there are three ─────────────────────────────────────────
 *   1. `ended` — the primary, and the only one that survives a locked screen.
 *   2. the rAF near-end check (`currentTime >= duration - 0.04`) — **Safari does not reliably fire
 *      `ended` for short blob-backed media** (§5.7). rAF is throttled to zero in a hidden tab, so
 *      this one is a foreground belt and nothing more.
 *   3. `timeupdate`, same near-end test — the belt for (1) failing *while hidden*, where (2) is dead.
 *      §5.5 forbids `timeupdate` for the **highlight**, because ~4 Hz with jitter lands the wash up
 *      to 250 ms late and reads as "the app is broken". Deciding that a page has finished is a
 *      different problem with a different tolerance: 250 ms late into 620 ms of deliberate silence is
 *      inaudible, and the alternative is a book that stops in a pocket at 1 a.m.
 * All three funnel through one **one-shot** `advance()`, armed once per page, so two triggers cannot
 * skip a page between them.
 *
 * ── LAW 12 ─────────────────────────────────────────────────────────────────────────────────────
 * Turning a page is browsing; it never moves the narration. Nothing in this file is reachable from a
 * page turn — `usePageTurn.ts` says the same thing from the other side ("if a future edit gives this
 * file a reference to an AudioEngine, LAW 12 is gone"). The narration moves the *page*, never the
 * other way, and it does it by emitting a tick the reader schedules against.
 *
 * ── LAW 14 ─────────────────────────────────────────────────────────────────────────────────────
 * The service worker must not claim `/api/v1/audio/`. This engine is built to need no SW: the blob
 * path bypasses one and the streaming path wants native ranged delivery. When P6 writes the SW, its
 * `fetch` handler opens with `if (url.pathname.startsWith('/api/v1/audio/')) return;` — no
 * `respondWith`. Safari requires a `206` for media; a cache-first SW returning a plain `200` breaks
 * playback, or plays once and then refuses to seek.
 *
 * ── Testability ────────────────────────────────────────────────────────────────────────────────
 * Nothing here touches a global. Elements, timers, rAF, `fetch` and the object-URL factory all arrive
 * through `EngineHost`, so `test/audio-engine.test.ts` drives the whole state machine under
 * `node --test` with fake elements — including the failures that only ever happen on a locked phone
 * on cellular, which are exactly the ones a browser run cannot reproduce on demand.
 */

import { cueIndexAt, findCue, fracInCue, resolveCueMs, type PageState } from "../../../shared/narration/cue.ts";
import { cachedAudioResponse } from "../lib/idb.ts";
import { installAirPlay } from "./airplay.ts";
import {
  armSleep,
  chapterSleepDue,
  SLEEP_OFF,
  sleepExpired,
  sleepVolume,
  stepSleep,
  type SleepChoice,
  type SleepState,
} from "./sleepTimer.ts";
import {
  browserMediaSession,
  browserWakeLock,
  MediaSessionBridge,
  ScreenWakeLock,
  type MediaActions,
  type NowPlayingBook,
  type VisibilityHost,
} from "./mediaSession.ts";
import {
  chooseSource,
  PageWindow,
  PREFETCH_AHEAD,
  primeIfUnhurried,
  primeTargets,
  samePageArtifact,
  type BlobHost,
  type EnginePage,
  type SourceKind,
} from "./pageWindow.ts";

export type { EnginePage } from "./pageWindow.ts";

// ─── the platform ────────────────────────────────────────────────────────────

/**
 * The part of `HTMLAudioElement` this engine uses. A real one satisfies it structurally, which is
 * what `test/audio-engine.test.ts` asserts with a one-line assignment — so a fake that drifts from
 * the real element is a type error rather than a green suite over a broken player.
 */
export interface AudioLike {
  src: string;
  currentTime: number;
  readonly duration: number;
  readonly paused: boolean;
  readonly ended: boolean;
  playbackRate: number;
  /**
   * The rate a `load()` resets `playbackRate` TO. Set alongside the rate itself, which is what makes
   * a page turn stop silently dropping a 1.5× listener back to 1.0.
   */
  defaultPlaybackRate?: number;
  preservesPitch?: boolean;
  /**
   * The sleep timer's fade. Optional because iOS ignores it entirely — writes are dropped and it
   * reads back 1 — which `canFade()` detects rather than assumes.
   */
  volume?: number;
  /** Safari used this spelling before 17.0, and a chipmunked cloned voice is not the feature. */
  webkitPreservesPitch?: boolean;
  preload: string;
  play(): Promise<void>;
  pause(): void;
  load(): void;
  /** The only spec-defined way to DETACH a media resource. `src = ""` loads the document instead. */
  removeAttribute(name: string): void;
  addEventListener(type: string, handler: () => void): void;
  removeEventListener(type: string, handler: () => void): void;
}

/** An opaque timer token. `number` in a browser, an object under Node — the engine never looks. */
export type TimerToken = unknown;

/**
 * The one-line surface of `BroadcastChannel` this engine uses.
 *
 * Injected rather than reached for, like everything else here, so `test/audio-engine.test.ts` can
 * drive two engines against one channel under `node --test`.
 */
export interface TabChannel {
  postMessage(message: unknown): void;
  addEventListener(type: "message", handler: (ev: { data: unknown }) => void): void;
  close(): void;
}

/** Everything outside this module. `BlobHost` brings `fetch` and the object-URL pair with it. */
export interface EngineHost extends BlobHost {
  createAudio(): AudioLike;
  /**
   * `new BroadcastChannel("ab-audio")`, or `null` where there is none.
   *
   * ⚠️ **Two tabs on one book both play, out loud, at once.** Each tab constructs its own engine at
   * module load, each unlocks, each plays, each writes `sessions.lastHeardAt` and each drives its own
   * prefetch — so a laptop with the book open twice reads two different pages simultaneously. P1
   * recorded "last-tab-backgrounded wins" for *positions* and called it harmless, which it was; with
   * audio it is not. One channel, one rule: whoever starts playing asks everybody else to stop.
   */
  tabChannel?(): TabChannel | null;
  raf(cb: () => void): number;
  cancelRaf(handle: number): void;
  setTimer(fn: () => void, ms: number): TimerToken;
  clearTimer(token: TimerToken): void;
}

// ─── what the engine is given ────────────────────────────────────────────────

/** A page the engine asked for and did not get. Never coerced into silence — see `waiting` below. */
export type PageResolution =
  | { ok: true; page: EnginePage }
  | {
      ok: false;
      state: PageState;
      etaSec: number | null;
      message?: string;
      /**
       * §4.8's `nothing-to-say`: a page that is an image, a table, or one the structure editor
       * marked silent. **Not a wait and not a failure** — there will never be audio for it, so the
       * prefetch must step over it rather than treat it as "the next page is not ready yet" and
       * park the boundary in `waitForNext` for a second of silence, once per plate.
       */
      silent?: boolean;
      /**
       * The refusal's code when the server gave one (`tts-unreachable`, `lane-unserved`, …). It is the
       * only thing that may turn a long wait into *"Couldn't reach the voice service"*: a page that
       * is merely queued behind a busy GPU is not a voice service that cannot be reached.
       */
      code?: string | null;
    };

/**
 * The reader's half of the contract: reading order, and how a page id becomes an artifact.
 *
 * Both are the reader's to answer, not the engine's. `pagesAfter` crosses chapters and knows where
 * the book ends; `pageAudio` is `POST /pages/:pageId/narration/ensure` (or the plan `GET`) with
 * `validatePageAudio()` already applied. The engine never constructs a URL, never guesses the next
 * page id, and never assumes a page exists — because a page id is only meaningful inside its edition
 * (LAW 1) and this file has no idea which edition is open.
 */
export interface EngineSource {
  /** The next `n` page ids after `pageId`, in reading order. `[]` at the end of the book. */
  pagesAfter(pageId: string, n: number): string[];
  /** The artifact for a page, or why there isn't one yet. */
  pageAudio(pageId: string): Promise<PageResolution>;
}

export type EngineStatus =
  /** Nothing loaded. */
  | "idle"
  /** A src is assigned and we are waiting for the browser. */
  | "loading"
  | "playing"
  | "paused"
  /** The page ended and the next one is still being generated (§4.9). Not a stall — a stated wait. */
  | "waiting"
  /** Autoplay was refused. The reader shows "Tap to start listening". */
  | "needs-gesture"
  | "error";

export type EngineEvent =
  | { type: "status"; status: EngineStatus }
  | { type: "page"; page: EnginePage; via: "open" | "advance" | "resume" | "artifact" }
  /** Every animation frame while playing. The read-along rides this rather than starting a second loop. */
  | { type: "tick"; masterMs: number; page: EnginePage }
  | { type: "waiting"; pageId: string; state: PageState; etaSec: number | null; waitedMs: number }
  | { type: "needs-gesture"; detail: string }
  | { type: "error"; message: string; detail: string }
  | { type: "end-of-book" }
  | { type: "rate"; rate: number }
  /** §4.11: a re-narration landed for the page that is playing. It is applied when you pause. */
  | { type: "artifact-deferred"; pageId: string }
  /** `visibilitychange → visible`. The reader resyncs the highlight and the page BEFORE paint. */
  | { type: "resync"; masterMs: number; page: EnginePage }
  /**
   * A moment worth saving as the `listen` position: a page boundary, or a pause (the listener's, the
   * OS's, or the sleep timer's). Fired from the boundary handler, which runs under lock — the only
   * place a locked phone can still say where it got to.
   */
  | { type: "listen-point"; reason: "advance" | "pause" }
  /** The sleep timer was armed, cancelled, or did its job. */
  | { type: "sleep"; state: SleepState; fired: boolean };

export type EngineListener = (ev: EngineEvent) => void;

// ─── the numbers ─────────────────────────────────────────────────────────────

/**
 * 10 ms of 8 kHz silence as a `data:` URI. Used only to unlock the elements inside a gesture.
 *
 * A `data:` URI and not a file, because the unlock must complete inside WebKit's transient activation
 * window and a network round trip does not fit inside it. 124 bytes: RIFF/`fmt `/`data`, no `LIST`
 * chunk, generated rather than copied.
 */
export const SILENT_WAV =
  "data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==";

/** §5.7's second end-of-page trigger, in seconds of media time. */
export const NEAR_END_SEC = 0.04;

/** `playbackRate` is clamped to this, and the clamp is the UI's contract too. */
export const MIN_RATE = 0.75;
export const MAX_RATE = 2.5;

/**
 * LAW 11's **secondary** signal, throttled. The primary is the audio proxy, which sees every page
 * fetch and touches `sessions.lastHeardAt` — the only liveness signal that survives a locked screen.
 *
 * This one is sent from the **segment-swap handler**, which fires while audio plays under lock, and
 * never from a timer: "a backgrounded Safari tab runs no timers, rAF is throttled to zero, and the
 * WebSocket is suspended within ~30 s of screen lock" (§4.9). A page is ~110 s of audio (measured),
 * so a 90 s floor means roughly one beat per page and never two for one swap.
 */
export const BEAT_MIN_MS = 90_000;

/**
 * How long we wait for a page before *"Couldn't reach the voice service"* — and only when the server
 * says the voice service is down (`TTS_DOWN_CODES`). A page queued behind a busy GPU is still coming,
 * and stopping the book over it is a lie told at 1 a.m.; past this we poll more slowly instead.
 */
export const WAIT_GIVE_UP_MS = 180_000;

/** The refusal codes that mean "nothing is going to make this page", as `narration.ts` spells them. */
export const TTS_DOWN_CODES: ReadonlySet<string> = new Set(["tts-unreachable", "tts-mode-none"]);

/** The poll ladder while a page generates. Client-side; §4.9's 2/8/32 ladder is the *job's* retry. */
const WAIT_POLL_MS = [1_200, 2_000, 3_500, 6_000, 8_000];

/** The poll once a wait has outlived `WAIT_GIVE_UP_MS` without the voice service being down. */
const WAIT_SLOW_POLL_MS = 15_000;

/**
 * While page N plays and N+1 is not yet in hand, re-ask this often — driven from `timeupdate`.
 *
 * ⚠️ **Not from a timer, and that is the fix.** The prefetch used to ask once when N started; if N+1
 * was still generating it never asked again until N *ended*, and then polled from `setTimeout` —
 * which iOS freezes the moment nothing is playing under a locked screen. The book stopped at the page
 * end and stayed stopped until somebody unlocked the phone. `timeupdate` keeps firing for as long as
 * N is audible, so asking from there means N+1 is usually primed before N runs out.
 */
export const AHEAD_RECHECK_MS = 12_000;

/** How long we let a draining element finish its last 40 ms before we take it for the next prefetch. */
const DRAIN_MS = 2_000;

// ─── the decks ───────────────────────────────────────────────────────────────

interface Deck {
  readonly el: AudioLike;
  readonly name: "a" | "b";
  page: EnginePage | null;
  src: string;
  kind: SourceKind | null;
  /** Set when this deck is playing out the tail of a page we have already advanced past. */
  draining: boolean;
  /**
   * ⚠️ Playback permission is granted **per element** on iOS, so it is tracked per element.
   *
   * An engine-wide flag set from one deck's state is a promise about the other deck that nothing
   * checked, and it is cashed exactly once — at the first page boundary, with the screen locked.
   */
  unlocked: boolean;
}

// ─── the engine ──────────────────────────────────────────────────────────────

export class AudioEngine {
  private readonly decks: [Deck, Deck];
  private at: 0 | 1 = 0;

  private readonly window: PageWindow;
  readonly media: MediaSessionBridge;
  readonly wakeLock: ScreenWakeLock;

  private source: EngineSource | null = null;
  private listeners = new Set<EngineListener>();

  private status: EngineStatus = "idle";
  /** What the *user* asked for, as opposed to what the element is doing while a page generates. */
  private intendPlaying = false;
  private unlocked = false;
  /**
   * The in-flight unlock, so a second caller joins it instead of starting a second one.
   *
   * One tap fires both `attachUnlockGesture`'s `pointerup` and the play button's `click`; two
   * concurrent unlocks would each snapshot the decks' `src` and each write its snapshot back.
   */
  private unlocking: Promise<boolean> | null = null;
  private suspendEvents = false;

  private rate = 1;
  private rafHandle = 0;
  private timers = new Set<TimerToken>();

  /**
   * Bumped on every page change and on every explicit open. Every `await` in this file re-checks it,
   * because a prefetch that resolves after the listener has jumped three chapters must not assign a
   * src to a deck that is now playing something else — a defect that appears only under a slow link
   * and looks exactly like "it randomly plays the wrong page".
   */
  private gen = 0;

  /** One-shot per page: `ended`, the rAF check and `timeupdate` all pass through it. */
  private advanceArmed = false;

  /** §4.11: a re-narration that landed while this page was playing, applied on the next pause. */
  private deferred: EnginePage | null = null;

  /** The idle deck holds the page we will swap to (or there is none to hold). Reset per page. */
  private aheadSettled = false;
  private aheadInFlight = false;
  private aheadSeq = 0;
  /** `host.now()` when the prefetch last asked. `timeupdate` re-asks `AHEAD_RECHECK_MS` after it. */
  private aheadAskedAt = Number.NEGATIVE_INFINITY;

  private sleepTimer: SleepState = SLEEP_OFF;
  /** `host.now()` at the previous progress report while playing; null across a pause. */
  private sleepLastAt: number | null = null;
  /**
   * Where a timer that cannot fade stops: the sentence playing when it expired. `undefined` while no
   * stop is pending; `null` is "expired in the silence between sentences", which stops at once.
   */
  private sleepStopSid: string | null | undefined = undefined;
  /** Whether element volume works here. iOS ignores it; probed once, never assumed. */
  private fadeOk: boolean | null = null;

  private pendingSeekMs: number | null = null;
  private sessionId: string | null = null;
  /** `-Infinity`, not 0: a clock that starts near zero would swallow the first beat of the session. */
  private lastBeatAt = Number.NEGATIVE_INFINITY;
  private nowPlaying: NowPlayingBook | null = null;
  private detachVisibility: (() => void) | null = null;
  private destroyed = false;
  private readonly tabs: TabChannel | null;
  /** This engine's identity on the channel, so it never answers its own broadcast. */
  private readonly tabId = `t${Math.random().toString(36).slice(2, 10)}`;

  constructor(private readonly host: EngineHost, opts?: { media?: MediaSessionBridge; wakeLock?: ScreenWakeLock }) {
    /**
     * ⚠️ **Both elements are created here, once, and here is module load.** They exist before any
     * gesture so that `unlock()` can touch *both* inside one — an element created later is an element
     * that was never unlocked, and it stalls silently on the page boundary that first uses it.
     */
    this.decks = [makeDeck(host.createAudio(), "a"), makeDeck(host.createAudio(), "b")];
    for (const deck of this.decks) {
      deck.el.preload = "auto";
      deck.el.addEventListener("ended", () => this.onEnded(deck));
      deck.el.addEventListener("timeupdate", () => this.onTimeUpdate(deck));
      deck.el.addEventListener("loadedmetadata", () => this.onLoadedMetadata(deck));
      deck.el.addEventListener("error", () => this.onElementError(deck));
      deck.el.addEventListener("play", () => this.onElementPlay(deck));
      deck.el.addEventListener("pause", () => this.onElementPause(deck));
    }

    /**
     * ⚠️ Constructed once, here, with the elements — not lazily on first play. A tab that opens the
     * book and does not press play must still HEAR the request to stop: the tab that is playing may
     * be the older one, and the newer one has to be able to tell it to give way.
     */
    this.tabs = host.tabChannel?.() ?? null;
    this.tabs?.addEventListener("message", (ev) => this.onTabMessage(ev.data));

    this.window = new PageWindow(host);
    if (opts?.media !== undefined) {
      this.media = opts.media;
    } else {
      const found = browserMediaSession();
      this.media = new MediaSessionBridge(found.session, found.metadata);
    }
    this.wakeLock = opts?.wakeLock ?? new ScreenWakeLock(browserWakeLock(), browserDocument());
  }

  // ─── what the reader reads ─────────────────────────────────────────────────

  get state(): EngineStatus {
    return this.status;
  }

  get playing(): boolean {
    return this.status === "playing";
  }

  get needsGesture(): boolean {
    return this.status === "needs-gesture" || !this.unlocked;
  }

  get currentPage(): EnginePage | null {
    return this.decks[this.at].page;
  }

  /** The element the read-along reads `currentTime` from. Exposed for §5.5's resync, nothing else. */
  get el(): AudioLike {
    return this.decks[this.at].el;
  }

  get playbackRate(): number {
    return this.rate;
  }

  /** LAW 11's id, as the server handed it back. Null until the first beat lands. */
  get session(): string | null {
    return this.sessionId;
  }

  /**
   * The **master clock**, in milliseconds on the page's own cue axis.
   *
   * `leadInMs` is subtracted here and in exactly one place, so no caller re-derives it. LAW 9 makes
   * it a required field precisely because `cue.start` and `el.currentTime` are measured from two
   * different zeros, and a coerced `?? 0` produces a highlight that is confidently a frame or two off
   * on every page, forever, with nothing to see. The read-along adds `CUE_LEAD_MS` on top of this —
   * the wash leads the voice; lagging reads as broken.
   */
  masterMs(): number {
    const page = this.currentPage;
    if (page === null) return 0;
    const t = this.decks[this.at].el.currentTime;
    if (!Number.isFinite(t)) return 0;
    return t * 1000 - page.audio.leadInMs;
  }

  subscribe(fn: EngineListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // ─── the gesture ───────────────────────────────────────────────────────────

  /**
   * Unlock both elements. **Called synchronously inside a `pointerup`.**
   *
   * ⚠️ Both `play()` calls are issued **before any `await`**. The original looped with
   * `await el.play()` inside, which ran the second `play()` after a task boundary — outside WebKit's
   * transient activation window. The symptom is page 2 stalling silently with nothing in the console,
   * which is precisely the failure the one-element camp was warning about, and it is invisible until
   * a real page boundary on a real phone.
   *
   * The pause loop comes **after** `allSettled` deliberately: pausing a pending `play()` rejects it
   * with "The play() request was interrupted by a call to pause()", and we would read our own tidying
   * as a refused autoplay.
   *
   * Whatever each element already had loaded is put back. A cold open assigns a src before the user
   * has tapped anything, and an unlock that silently discarded it would re-download the page the
   * listener is about to hear — which is the exact dead air §5.7 chose `+faststart` to avoid.
   *
   * ⚠️ **SINGLE-FLIGHT, AND IT MUST NOT CLOBBER WHAT LANDED WHILE IT WAS IN FLIGHT.** Both halves were
   * found by the first end-to-end run in a real browser, and together they were the only thing
   * stopping P2's gate.
   *
   * One tap on the play button fires **two** things: `attachUnlockGesture`'s `pointerup` (this
   * function) and the button's own `click` (`open()` → `play()`). They overlap. The original code
   * snapshotted `restore = decks.map(d => d.el.src)` — both `""` on a cold open — then, tens of
   * milliseconds later, wrote that snapshot back over the artifact `open()` had assigned in between.
   * The element ended holding an empty `src`, which the browser reports as
   * `MEDIA_ELEMENT_ERROR: Empty src attribute`, which `onElementError` then reported to the listener
   * as *"This page's audio couldn't be played."* Nothing was wrong with the audio. Measured: play
   * pressed at t=2265 ms, `open` at 2273, two `needs-gesture` AbortErrors at 2298, `error` at 2301.
   *
   * So: a second caller joins the in-flight unlock instead of starting another, and each deck is
   * restored **only if it is still holding the silent WAV** — i.e. only if nothing else claimed it.
   */
  unlock(): Promise<boolean> {
    if (this.unlocked) return Promise.resolve(true);
    if (this.destroyed) return Promise.resolve(false);
    if (this.unlocking !== null) return this.unlocking;
    this.unlocking = this.runUnlock().finally(() => {
      this.unlocking = null;
    });
    return this.unlocking;
  }

  /**
   * ⚠️ **iOS grants playback permission PER ELEMENT, so the unlock is tracked per deck.**
   *
   * The original short-circuited on `!decks[at].el.paused` — "something is already making sound, so
   * the elements are demonstrably unlocked" — and set the engine-wide flag from ONE element's state.
   * That reasoning is true of the deck that is playing and says nothing at all about the other one,
   * and the sequence that exploits it is ordinary: the first `unlock()` is refused (Low Power Mode, a
   * per-site "Auto-Play: Never", or the `AbortError` race below), the play button's own `click` still
   * starts deck A because that click IS a gesture, and the next `pointerup` — the listener's first
   * swipe — walks into this branch and declares the engine unlocked. Deck B has never been played
   * inside any gesture. At the end of page one `advance()` calls `other.el.play()`, it rejects with
   * `NotAllowedError`, and the book stops at its first page boundary with the screen locked. Which is
   * P2's gate, and it looks like nothing at all in a console.
   */
  private async runUnlock(): Promise<boolean> {
    const restore = this.decks.map((d) => d.el.src);
    this.suspendEvents = true;
    const started = this.decks.map((d) => {
      // A deck that is already making sound is already granted, and interrupting it to prove a fact we
      // have would clip the audio the listener is hearing. Marked, skipped, and — crucially — the
      // OTHER deck still gets its silent `play()` inside this same gesture.
      if (!d.el.paused) {
        d.unlocked = true;
        return null;
      }
      d.el.src = SILENT_WAV;
      return d.el.play();
    });

    const results = await Promise.allSettled(started.map((p) => p ?? Promise.resolve()));

    for (const d of this.decks) {
      try {
        d.el.pause();
        d.el.currentTime = 0;
      } catch {
        /* an element that refuses to rewind 10 ms of silence is not a reason to fail an unlock */
      }
    }
    this.decks.forEach((d, i) => {
      /**
       * Restore ONLY what we replaced. If this element is no longer holding the silent WAV, something
       * assigned it a real page while we were awaiting — `open()` from the same tap — and putting the
       * snapshot back would discard the artifact the listener is waiting to hear. See the header.
       */
      if (d.el.src !== SILENT_WAV) return;
      const was = restore[i] ?? "";
      d.el.src = was;
      if (was !== "") {
        d.el.load();
        // `load()` resets `playbackRate` to `defaultPlaybackRate` synchronously, so a 1.5× listener
        // would silently drop to 1.0 for the rest of the page. `applyRate` sets both.
        this.applyRate(d.el);
      }
    });
    this.suspendEvents = false;

    /**
     * ⚠️ **An `AbortError` is not a refused autoplay** — the same distinction `play()` makes forty
     * lines below, and the same one this function got wrong. `NotAllowedError` is the browser
     * refusing; `AbortError` means *another operation interrupted this play*, which here is `open()`
     * from the same tap, `stopDeck`, or the two `play()`s racing each other. Read as a refusal it
     * sets `unlocked = false` over an element that is perfectly granted, puts "Tap to start
     * listening" on screen a moment after the listener tapped to start listening, and — through the
     * per-deck flag above — is the entry point to the silent stall at the first page boundary.
     */
    const refused = results.find(
      (r) => r.status === "rejected" && (r.reason as { name?: string } | null)?.name !== "AbortError",
    );
    this.decks.forEach((d, i) => {
      if (results[i]?.status === "fulfilled") d.unlocked = true;
      else if ((results[i] as PromiseRejectedResult | undefined)?.reason?.name === "AbortError") d.unlocked = true;
    });

    if (refused !== undefined) {
      this.unlocked = false;
      const detail = String((refused as PromiseRejectedResult).reason);
      this.setStatus("needs-gesture");
      this.emit({ type: "needs-gesture", detail });
      return false;
    }

    // Engine-wide only when BOTH elements have been played inside a gesture. Anything weaker is a
    // promise about deck B made from deck A's evidence.
    this.unlocked = this.decks.every((d) => d.unlocked);
    if (this.unlocked && this.status === "needs-gesture") {
      this.setStatus(this.currentPage === null ? "idle" : "paused");
    }
    return this.unlocked;
  }

  /**
   * Unlock on the **first `pointerup` anywhere in the reader**, not just on the play button.
   *
   * By the time someone taps play they have almost always already touched the page — a swipe, a tap
   * in a turn zone, the chapter drawer. Unlocking then means the play button starts sound in the same
   * frame it is pressed, rather than spending its own gesture on the unlock.
   *
   * Capture phase, so it runs before the reader's own handlers; and it stays attached until an unlock
   * actually succeeds, because a refusal (Low Power Mode, a Safari setting) must be retried on the
   * next touch rather than costing the listener the session.
   */
  attachUnlockGesture(target: GestureTarget): () => void {
    const onPointerUp = (): void => {
      if (this.unlocked || this.destroyed) {
        detach();
        return;
      }
      void this.unlock().then((ok) => {
        if (ok) detach();
      });
    };
    let attached = true;
    const detach = (): void => {
      if (!attached) return;
      attached = false;
      target.removeEventListener("pointerup", onPointerUp, true);
    };
    target.addEventListener("pointerup", onPointerUp, true);
    return detach;
  }

  // ─── wiring ────────────────────────────────────────────────────────────────

  setSource(source: EngineSource): void {
    this.source = source;
  }

  /** The book, for the lock screen. The chapter half moves with the narration. */
  setNowPlaying(book: NowPlayingBook, actions?: Partial<MediaActions>): void {
    this.nowPlaying = book;
    this.media.setBook(book);
    this.media.bindActions({
      play: () => void this.play(),
      pause: () => this.pause(),
      skip: (sec) => this.skip(sec),
      ...actions,
    });
  }

  /**
   * `visibilitychange → visible`: §5.5's "one synchronous resync before paint".
   *
   * The engine's own half is small — rAF callbacks are not delivered to a hidden tab, so the loop may
   * be sitting on a frame that will never arrive — and the reader's half is the point: recompute the
   * cue index from `currentTime`, jump the highlight, jump the page with `animate:false`, no
   * catching-up animation. The event carries the master clock so the reader does not have to ask.
   */
  attachVisibilityResync(doc: VisibilityHost): () => void {
    // A second call replaces the first rather than stacking: React strict mode mounts effects twice,
    // and two listeners means two resyncs per wake, each one jumping the page.
    this.detachVisibility?.();
    const onChange = (): void => {
      if (doc.visibilityState !== "visible" || this.destroyed) return;
      const page = this.currentPage;
      if (page !== null) this.emit({ type: "resync", masterMs: this.masterMs(), page });
      if (this.status === "playing" && this.rafHandle === 0) this.startFrames();
    };
    doc.addEventListener("visibilitychange", onChange);
    this.detachVisibility = () => doc.removeEventListener("visibilitychange", onChange);
    return this.detachVisibility;
  }

  // ─── loading a page ────────────────────────────────────────────────────────

  /**
   * Put a page on the current deck. The reader calls this on open, on a seek to another page, and
   * when the listener asks to play from a sentence.
   *
   * ⚠️ **Re-opening the page that is already playing is a no-op that touches no `src`.** This is the
   * acceptance test for the whole reader hiding in one `if`: change the font size mid-sentence and
   * the reader re-renders, re-measures and calls back in with the same page — and the audio must not
   * seek or gap. Reassigning an identical `src` is not free on Safari; it is a reload, a re-buffer
   * and an audible hole in the middle of a sentence. Layout and playback share nothing but a `sid`,
   * and this is where that stays true.
   */
  async open(page: EnginePage, opts?: { at?: { sid: string; frac: number }; autoplay?: boolean }): Promise<void> {
    if (this.destroyed) return;
    // Same tap, two handlers: wait for the unlock to put the elements back before assigning a src to
    // one of them, rather than having it restore its snapshot over what we assign. See `unlock()`.
    if (this.unlocking !== null) await this.unlocking;
    if (this.destroyed) return;
    const cur = this.decks[this.at];

    // The `error` exception is what makes a Retry reachable: after a media error on this page the
    // element is holding a src it could not decode, and returning early because the *page* has not
    // changed would make the retry button do nothing at all.
    if (samePageArtifact(cur.page, page) && this.status !== "error") {
      if (opts?.at !== undefined) this.seekToSid(opts.at.sid, opts.at.frac);
      if (opts?.autoplay === true) await this.play();
      return;
    }

    const other = this.decks[1 - this.at];
    const gen = ++this.gen;

    // Already primed on the idle deck — a listener who turned forward one page and pressed play.
    // Swapping is instant and costs no network; reloading the current deck would cost both.
    if (samePageArtifact(other.page, page) && other.src !== "") {
      this.stopDeck(cur);
      this.at = (1 - this.at) as 0 | 1;
      this.becomeCurrent("open");
      if (opts?.at !== undefined) this.seekToSid(opts.at.sid, opts.at.frac);
      // ⚠️ **Rewind when no target was given.** After `advance()` this deck holds the page it just
      // finished, with `currentTime` sitting at its duration — so opening it without seeking replays
      // the last 40 ms and immediately re-advances, which reads as "the book randomly jumps". The
      // caller only supplies `at` when the sid is in the cue list, so tapping a non-speakable
      // sentence on the page you just left takes exactly this path.
      else this.seekMasterMs(0);
      if (opts?.autoplay === true) await this.play();
      void this.prepareAhead(this.gen);
      return;
    }

    this.stopDeck(cur);
    this.setStatus("loading");
    // Urgent: a sample has to come out of the speaker as soon as possible, so we stream from the
    // network unless the bytes happen to be in hand already (§5.7).
    const primed = await primeIfUnhurried(this.window, page, true);
    if (gen !== this.gen || this.destroyed) return;

    this.assign(cur, page, chooseSource(page, primed));
    this.becomeCurrent("open");
    if (opts?.at !== undefined) this.seekToSid(opts.at.sid, opts.at.frac);
    else this.pendingSeekMs = null;

    if (opts?.autoplay === true) await this.play();
    void this.prepareAhead(this.gen);
  }

  /**
   * §4.11: a page was re-narrated. New clip → new `artifactKey` → new artifact, new cues, new URL.
   *
   * **The currently-playing page is never swapped.** Refetching 300 KB and reassigning `src`
   * mid-sentence is an audible gap and, on iOS, a chance of losing the Now Playing session — so a fix
   * that lands while this page is playing is recorded and applied the moment playback stops. A fix
   * for the page ahead is applied immediately: nothing is playing on that deck, so it costs nothing
   * and the alternative is playing the stale artifact at the next boundary because we happened to
   * resolve it thirty seconds too early.
   *
   * Position is preserved by `sid` and `frac`, never by seconds (LAW 3) — a seconds-based restore
   * lands mid-word, and the whole point of per-sentence clips is that it does not have to.
   */
  async applyArtifact(page: EnginePage): Promise<void> {
    if (this.destroyed) return;
    const cur = this.decks[this.at];
    const other = this.decks[1 - this.at];

    if (cur.page !== null && cur.page.audio.pageId === page.audio.pageId) {
      if (samePageArtifact(cur.page, page)) return;
      if (this.status === "playing") {
        this.deferred = page;
        this.emit({ type: "artifact-deferred", pageId: page.audio.pageId });
        return;
      }
      await this.swapArtifactInPlace(page);
      return;
    }

    this.window.release(page.audio.pageId);
    if (other.page !== null && other.page.audio.pageId === page.audio.pageId) {
      const gen = ++this.gen;
      const primed = await primeIfUnhurried(this.window, page, false);
      if (gen !== this.gen || this.destroyed) return;
      this.assign(other, page, chooseSource(page, primed));
    }
  }

  private async swapArtifactInPlace(page: EnginePage): Promise<void> {
    const cur = this.decks[this.at];
    const old = cur.page;
    const gen = ++this.gen;

    // Where we are, as a sentence and a fraction of it — resolved against the cue list we are
    // holding, before it is replaced. `-1` is the `pageHead` silence, where there is no sentence to
    // be inside and the honest restore is the head of the page.
    let restore: { sid: string; frac: number } | null = null;
    if (old !== null) {
      const ms = this.masterMs();
      const i = cueIndexAt(old.audio.cues, ms);
      const cue = i < 0 ? undefined : old.audio.cues[i];
      if (cue !== undefined) restore = { sid: cue.sid, frac: fracInCue(cue, ms) };
    }

    const primed = await primeIfUnhurried(this.window, page, true);
    if (gen !== this.gen || this.destroyed) return;

    this.assign(cur, page, chooseSource(page, primed));
    this.becomeCurrent("artifact");
    if (restore !== null) this.seekToSid(restore.sid, restore.frac);
    this.deferred = null;
  }

  // ─── transport ─────────────────────────────────────────────────────────────

  async play(): Promise<boolean> {
    if (this.destroyed) return false;
    const deck = this.decks[this.at];
    if (deck.page === null) return false;

    this.intendPlaying = true;
    // Waiting for the GPU is not a paused state and pressing play does not make a page exist. The
    // wait is already running and will resume itself; saying so beats a play button that does nothing.
    if (this.status === "waiting") return false;

    // A tap that reaches the play button also reaches `attachUnlockGesture`. Joining the in-flight
    // unlock rather than racing it is what keeps `pause()`-during-`play()` from happening at all;
    // `unlock()` is a data: URI and resolves in a millisecond or two.
    if (this.unlocking !== null) await this.unlocking;
    if (this.destroyed || deck !== this.decks[this.at]) return false;

    try {
      await deck.el.play();
    } catch (err) {
      /**
       * ⚠️ **An `AbortError` is not a refused autoplay, and treating it as one is why P2's gate
       * failed on its first real browser run.**
       *
       * The DOM has two different rejections here. `NotAllowedError` means the browser refused —
       * that is the one "Tap to start listening" is for. `AbortError` means *another operation
       * interrupted this play*: a `pause()`, a new `src`, a `load()`. It is a race, it is usually
       * ours, and the correct response is to say nothing and let whatever interrupted us proceed.
       * Reported as `needs-gesture` it puts "tap to start listening" on screen immediately after the
       * listener tapped to start listening.
       */
      if ((err as { name?: string })?.name === "AbortError") return false;
      this.setStatus("needs-gesture");
      this.emit({ type: "needs-gesture", detail: String(err) });
      return false;
    }
    this.setStatus("playing");
    this.sleepLastAt = null;
    this.startFrames();
    this.media.setPlaybackState("playing");
    this.syncPosition();
    this.claimAudio();
    void this.beat(true);
    return true;
  }

  /**
   * "I am the tab making sound now." Broadcast on every start, never on a page boundary — the
   * boundary is the same tab continuing, and a message there would be a wakeup per page for nothing.
   */
  private claimAudio(): void {
    try {
      this.tabs?.postMessage({ kind: "playing", from: this.tabId });
    } catch {
      /* a channel that is closing is not a reason to lose the page */
    }
  }

  private onTabMessage(data: unknown): void {
    const msg = data as { kind?: unknown; from?: unknown } | null;
    if (msg?.kind !== "playing" || msg.from === this.tabId || this.destroyed) return;
    // Another tab started. Yield rather than compete: two voices reading two different pages out of
    // the same laptop is not a state anybody wants to be in, and the tab that just acted is the one
    // the listener is looking at.
    if (this.status === "playing") this.pause();
  }

  pause(): void {
    if (this.destroyed) return;
    // Captured before `el.pause()`, whose own `pause` event moves the status first.
    const was = this.status;
    this.intendPlaying = false;
    this.decks[this.at].el.pause();
    this.setStatus("paused");
    this.stopFrames();
    this.sleepLastAt = null;
    this.media.setPlaybackState("paused");
    this.syncPosition();
    // ⚠️ Only a pause of something that was actually being heard is a place worth saving. A lock-screen,
    // Bluetooth or room pause reaching an idle tab would otherwise write that tab's stale sentence
    // with a fresh `clientUpdatedAt` — and newest-wins would reopen the book there.
    if (was === "playing" || was === "waiting") this.emit({ type: "listen-point", reason: "pause" });
    // §4.11's "an immediate swap only when paused" — this is that moment.
    const pending = this.deferred;
    if (pending !== null) {
      this.deferred = null;
      void this.swapArtifactInPlace(pending);
    }
  }

  toggle(): void {
    if (this.playing) this.pause();
    else void this.play();
  }

  /**
   * The Retry behind §4.9's *"Couldn't reach the voice service. The book still reads."*
   *
   * It re-enters the wait for the **next** page, which is the only thing that can be stuck: a page
   * that failed to decode on the current deck is retried by `open()`ing it again, and `open()` has an
   * explicit exception for the error state so that it can be.
   */
  retryNextPage(): void {
    if (this.status !== "error" && this.status !== "waiting") return;
    this.intendPlaying = true;
    void this.waitForNext();
  }

  /**
   * Seek to a sentence on the **current page**. Returns false when the sid is not on it.
   *
   * ⚠️ **Never snapped to the nearest cue.** A read-along that plays one sentence while highlighting
   * another does not look broken — it makes the listener think they misread. If the sid is elsewhere
   * in the book, the caller loads that page and passes the sid to `open()`; there is no fallback here
   * because every available fallback is a lie.
   */
  seekToSid(sid: string, frac = 0): boolean {
    const page = this.currentPage;
    if (page === null) return false;
    const cue = findCue(page.audio.cues, sid);
    if (cue === undefined) return false;
    this.seekMasterMs(resolveCueMs(cue, frac));
    return true;
  }

  /** Seek on the page's own cue axis. `leadInMs` is added back exactly here and nowhere else. */
  seekMasterMs(ms: number): void {
    const page = this.currentPage;
    if (page === null || !Number.isFinite(ms)) return;
    const target = Math.max(0, ms + page.audio.leadInMs) / 1000;
    const deck = this.decks[this.at];
    if (!Number.isFinite(deck.el.duration)) {
      // Metadata has not arrived; Safari drops a `currentTime` written before it does. Held and
      // applied on `loadedmetadata`, which is also where the rate is re-applied for the same reason.
      this.pendingSeekMs = ms;
      return;
    }
    deck.el.currentTime = target;
    this.rearmAdvance();
    this.syncPosition();
  }

  /**
   * ±N seconds, clamped to this page.
   *
   * A lock-screen skip that runs off the end of a page needs the *next* page's artifact, which this
   * file cannot fetch synchronously inside a Media Session handler — so out-of-page skips are handed
   * to the app if it supplied a handler and clamped if it did not. Clamping is visible and
   * recoverable; guessing a page id is not.
   */
  skip(seconds: number): void {
    const page = this.currentPage;
    if (page === null || !Number.isFinite(seconds)) return;
    const deck = this.decks[this.at];
    const now = Number.isFinite(deck.el.currentTime) ? deck.el.currentTime : 0;
    const dur = Number.isFinite(deck.el.duration) ? deck.el.duration : page.audio.durationMs / 1000;
    const target = Math.min(Math.max(now + seconds, 0), Math.max(dur - 0.05, 0));
    deck.el.currentTime = target;
    this.rearmAdvance();
    this.syncPosition();
  }

  /**
   * 0.75–2.5. **Rate does not affect cue math at all** — cues are in media time, and media time is
   * what `currentTime` reports whatever the rate is.
   *
   * `preservesPitch` goes on with it: without it, 1.5× is a chipmunk, and a cloned voice sped up
   * into a different larynx is not the feature.
   */
  setRate(rate: number): void {
    const clamped = Math.min(Math.max(Number.isFinite(rate) ? rate : 1, MIN_RATE), MAX_RATE);
    this.rate = clamped;
    for (const deck of this.decks) this.applyRate(deck.el);
    this.emit({ type: "rate", rate: clamped });
    this.syncPosition();
  }

  private applyRate(el: AudioLike): void {
    // ⚠️ Re-applied on every `loadedmetadata`: neither survives an `src` change, so a page turn
    // silently drops the listener back to 1.0 and the fix looks like "it resets randomly".
    try {
      el.playbackRate = this.rate;
      /**
       * `defaultPlaybackRate` is what the media load algorithm sets `playbackRate` back TO, and it
       * does so **synchronously inside `load()`** — before any `loadedmetadata` can re-apply anything.
       * Setting it here makes every future `load()` a no-op for the rate and removes the whole class,
       * rather than racing it once per page.
       */
      el.defaultPlaybackRate = this.rate;
      el.preservesPitch = true;
      // Safari shipped the unprefixed property in 17.0 and used this before it. WebKit defaults it to
      // true, so this is belt rather than braces — and it costs one assignment.
      el.webkitPreservesPitch = true;
    } catch {
      /* an element that refuses a legal rate is not worth losing the page over */
    }
  }

  // ─── where the listener is ─────────────────────────────────────────────────

  /**
   * The sentence being heard, as `{sid, frac}` on the current page's own cue list (LAW 3) — what a
   * `listen` position stores. In the `pageHead` silence it is the page's first sentence at 0.
   */
  heardAt(): { sid: string; frac: number; page: EnginePage } | null {
    const page = this.currentPage;
    if (page === null) return null;
    const cues = page.audio.cues;
    const ms = this.masterMs();
    const i = cueIndexAt(cues, ms);
    const cue = cues[i < 0 ? 0 : i];
    if (cue === undefined) return null;
    return { sid: cue.sid, frac: i < 0 ? 0 : fracInCue(cue, ms), page };
  }

  /** The sid under the playhead, or null in the silence before the page's first sentence. */
  private sidNow(): string | null {
    const page = this.currentPage;
    if (page === null) return null;
    const i = cueIndexAt(page.audio.cues, this.masterMs());
    return i < 0 ? null : (page.audio.cues[i]?.sid ?? null);
  }

  // ─── the sleep timer ───────────────────────────────────────────────────────

  /** Arm (`15 | 30 | 45 | 60 | "chapter"`) or cancel (`null`). */
  setSleep(choice: SleepChoice | null): void {
    if (this.destroyed) return;
    const page = this.currentPage;
    // "End of chapter" needs a chapter; with nothing loaded there is nothing for it to mean.
    this.sleepTimer =
      choice === null || (choice === "chapter" && page === null) ? SLEEP_OFF : armSleep(choice, page?.chapterOrd ?? -1);
    this.sleepLastAt = null;
    this.sleepStopSid = undefined;
    this.setVolume(1);
    this.emit({ type: "sleep", state: this.sleepTimer, fired: false });
  }

  /** What the timer is doing, for the playback bar. `stopping`: expired, finishing the sentence. */
  get sleepState(): SleepState & { stopping: boolean } {
    return { ...this.sleepTimer, stopping: this.sleepStopSid !== undefined };
  }

  /**
   * One progress report's worth of sleep timer. Called from `timeupdate` while playing.
   *
   * Where the element's volume works the last `SLEEP_FADE_MS` fade out and the pause lands at
   * silence. **iOS ignores element volume** — a fade there is twenty seconds of nothing followed by
   * a hard cut mid-word — so there the expiry is held until the sentence that was playing ends.
   */
  private tickSleep(): void {
    if (this.sleepTimer.kind !== "minutes") return;
    const now = this.host.now();
    const last = this.sleepLastAt;
    this.sleepLastAt = now;
    if (last !== null) this.sleepTimer = stepSleep(this.sleepTimer, now - last);

    if (this.sleepStopSid !== undefined) {
      if (this.sleepStopSid === null || this.sidNow() !== this.sleepStopSid) this.fireSleep();
      return;
    }
    const fade = this.canFade();
    if (fade) this.setVolume(sleepVolume(this.sleepTimer));
    if (!sleepExpired(this.sleepTimer)) return;
    if (fade) {
      this.fireSleep();
      return;
    }
    this.sleepStopSid = this.sidNow();
    if (this.sleepStopSid === null) this.fireSleep();
  }

  private fireSleep(): void {
    this.sleepTimer = SLEEP_OFF;
    this.sleepStopSid = undefined;
    this.sleepLastAt = null;
    // `pause()` writes the listen position; the volume comes back only once nothing is playing.
    this.pause();
    this.setVolume(1);
    this.emit({ type: "sleep", state: this.sleepTimer, fired: true });
  }

  /** Does element volume do anything here? Probed by writing it and reading it back, once. */
  private canFade(): boolean {
    if (this.fadeOk !== null) return this.fadeOk;
    const el = this.decks[this.at].el;
    if (typeof el.volume !== "number") return (this.fadeOk = false);
    try {
      const was = el.volume;
      const probe = was === 0.5 ? 0.6 : 0.5;
      el.volume = probe;
      this.fadeOk = Math.abs(el.volume - probe) < 0.01;
      el.volume = was;
    } catch {
      this.fadeOk = false;
    }
    return this.fadeOk;
  }

  /** Both decks: a boundary in the middle of a fade must not come back at full volume. */
  private setVolume(v: number): void {
    for (const deck of this.decks) {
      if (typeof deck.el.volume !== "number" || deck.el.volume === v) continue;
      try {
        deck.el.volume = v;
      } catch {
        /* a fade is decoration; the pause is the feature */
      }
    }
  }

  // ─── the swap ──────────────────────────────────────────────────────────────

  private onEnded(deck: Deck): void {
    if (this.suspendEvents) return;
    if (deck.draining) {
      deck.draining = false;
      return;
    }
    if (deck !== this.decks[this.at]) return;
    this.advance();
  }

  private onTimeUpdate(deck: Deck): void {
    if (this.suspendEvents || deck !== this.decks[this.at]) return;
    if (this.nearEnd(deck)) {
      this.advance();
      return;
    }
    // ~4 Hz, and it keeps firing under a locked screen for as long as the voice does — which is what
    // makes it the one clock both of these can trust there. Neither may run off a timer.
    if (this.status !== "playing") return;
    this.tickSleep();
    this.recheckAhead();
  }

  private nearEnd(deck: Deck): boolean {
    const d = deck.el.duration;
    if (!Number.isFinite(d) || d <= 0) return false;
    return deck.el.currentTime >= d - NEAR_END_SEC;
  }

  /**
   * The page boundary. **One-shot**, armed once per page: `ended`, the rAF check and `timeupdate` all
   * arrive here and only the first one does anything, or a fast trigger pair skips a page between
   * them and nobody can tell you why the book jumped.
   */
  private advance(): void {
    if (!this.advanceArmed || this.destroyed) return;
    const cur = this.decks[this.at];
    if (cur.page === null) return;
    this.advanceArmed = false;

    const other = this.decks[1 - this.at];
    /**
     * ⚠️ **The idle deck must not still be holding the page we just finished.**
     *
     * `prepareAhead` reassigns it, but it first waits for `whenFree` — the draining deck is still
     * playing the last 40 ms of the page the listener is hearing, and assigning a `src` to it would
     * clip the final consonant of every page. If a boundary arrives before that wait resolves (a
     * prefetch that is still on the network, a page that was already generating), the deck is holding
     * page N and `other.page !== null && other.src !== ""` was enough to swap onto it — so the book
     * played page N, then played page N again, with `status: playing` and nothing in the console.
     * Reproduced in `test/audio-engine.test.ts` on two consecutive rAF-driven boundaries.
     *
     * The honest alternative is `waitForNext`, which polls, says "generating the next page", and
     * resumes by itself. Never guessed, and never the page we just left.
     */
    const ready = other.page !== null && other.src !== "" && other.page.audio.pageId !== cur.page.audio.pageId;

    if (ready) {
      /**
       * ⚠️ **`play()` first, before any other statement.** B was unlocked in the same gesture as A, so
       * this is allowed without a gesture of its own — but it must still be the first thing that
       * happens in this turn of the event loop. Everything below it is bookkeeping and can wait; a
       * `play()` that has drifted behind an `await` is the silent stall on page 2.
       */
      const started = other.el.play();

      // The old deck keeps its last 40 ms rather than being cut off: on the near-end path it has not
      // finished, and the new page opens with 620–900 ms of `pageHead` silence (§4.2), so the overlap
      // is inaudible while a `pause()` here would clip the final consonant of the page.
      cur.draining = !cur.el.paused && !cur.el.ended;

      this.at = (1 - this.at) as 0 | 1;
      this.becomeCurrent("advance");
      this.applyRate(other.el);

      void started.catch((err: unknown) => {
        // An `AbortError` is something of ours interrupting the play — the sleep timer pausing at a
        // chapter edge in this very turn — and not a refusal (see `play()`).
        if ((err as { name?: string } | null)?.name === "AbortError") return;
        // The element was unlocked, so a refusal here is iOS having torn the audio session down while
        // we waited. Say so; do not pretend the book is still reading.
        this.setStatus("needs-gesture");
        this.emit({ type: "needs-gesture", detail: String(err) });
      });

      this.setStatus("playing");
      this.startFrames();
      this.media.setPlaybackState("playing");
      void this.beat(true);
      void this.prepareAhead(this.gen);
      // Under lock this handler is the only code that runs at a boundary, so it is where the place
      // gets saved — and where an end-of-chapter sleep timer gets its chance, inside the new page's
      // deliberate `pageHead` silence.
      if (chapterSleepDue(this.sleepTimer, other.page?.chapterOrd ?? cur.page.chapterOrd)) this.fireSleep();
      else this.emit({ type: "listen-point", reason: "advance" });
      return;
    }

    // Nothing primed: either the next page is still on the GPU, or a prefetch failed. Neither is a
    // stop — §4.9: "playback does not silently stop and there is no bare spinner."
    void this.waitForNext();
  }

  /**
   * §4.9's wait, with a deadline.
   *
   * The reader shows *"generating the next page — about 12 seconds"* from `etaSec`, the page holds,
   * the text stays readable, and it auto-resumes on `ready`. If the page never arrives we say the
   * true thing — *"Couldn't reach the voice service. The book still reads."* — rather than polling a
   * dead server until the battery is gone.
   */
  private async waitForNext(): Promise<void> {
    const source = this.source;
    const cur = this.decks[this.at];
    if (source === null || cur.page === null) return;

    const from = cur.page.audio.pageId;
    const gen = this.gen;
    /**
     * ⚠️ **The deadline is measured in time we were actually AWAKE, not in wall clock.**
     *
     * Once we are in here nothing is playing, so iOS freezes the tab within ~30 s of the screen
     * locking and `sleep()`'s `setTimeout` does not run again until the user unlocks. Differencing
     * `now()` then charges the whole locked interval to the wait: come back five minutes later and
     * the first poll computes `waited ≈ 300 s > WAIT_GIVE_UP_MS`, and shows *"Couldn't reach the
     * voice service"* for a page that has been ready for four and a half minutes. The only recovery
     * is a Retry the listener has no reason to trust.
     *
     * Summing the poll delays we intended to sleep, plus the wall time each network round trip really
     * took, is the honest measure of "how long have we been trying" and is unaffected by a frozen tab.
     */
    let waited = 0;
    this.setStatus("waiting");
    this.stopFrames();
    // The page we finished is where the listener is, as far as anybody can know with the screen off.
    this.emit({ type: "listen-point", reason: "advance" });

    for (let attempt = 0; ; attempt++) {
      if (gen !== this.gen || this.destroyed) return;

      const ids = source.pagesAfter(from, 1);
      const nextId = ids[0];
      if (nextId === undefined) {
        this.endOfBook();
        return;
      }

      const askedAt = this.host.now();
      const res = await this.resolvePage(nextId);
      if (gen !== this.gen || this.destroyed) return;
      // A round trip is real elapsed time and the tab was awake for it, so it counts. A tab frozen
      // between two polls is not.
      waited += Math.max(0, this.host.now() - askedAt);

      if (res.ok) {
        await this.resumeInto(res.page, gen);
        return;
      }

      if (res.state === "failed") {
        this.fail(
          res.message ?? "This page couldn't be narrated. The book still reads, and you can retry it from the sentence menu.",
          `page ${nextId} failed`,
        );
        return;
      }

      this.emit({ type: "waiting", pageId: nextId, state: res.state, etaSec: res.etaSec, waitedMs: waited });
      /**
       * ⚠️ **Only a server that SAYS the voice is down ends the wait.** Three minutes used to be the
       * whole test, and a GPU busy with somebody else's chapter — the normal, designed state on one
       * card — put *"Couldn't reach the voice service"* on screen over a page that arrived a minute
       * later and that nothing was then waiting to play. Past the deadline a busy GPU is polled more
       * slowly; an unreachable one is named.
       */
      const down = res.code != null && TTS_DOWN_CODES.has(res.code);
      if (waited > WAIT_GIVE_UP_MS && down) {
        this.fail("Couldn't reach the voice service. The book still reads.", `gave up on ${nextId} after ${Math.round(waited / 1000)}s`);
        return;
      }
      const poll =
        waited > WAIT_GIVE_UP_MS ? WAIT_SLOW_POLL_MS : (WAIT_POLL_MS[Math.min(attempt, WAIT_POLL_MS.length - 1)] ?? 8_000);
      waited += poll;
      await this.sleep(poll);
    }
  }

  /** The page finally arrived. Urgent, so we stream it — the listener has already been waiting. */
  private async resumeInto(page: EnginePage, gen: number): Promise<void> {
    const primed = await primeIfUnhurried(this.window, page, true);
    if (gen !== this.gen || this.destroyed) return;

    const cur = this.decks[this.at];
    this.stopDeck(cur);
    this.assign(cur, page, chooseSource(page, primed));
    this.becomeCurrent("resume");
    // Out of `waiting` before `play()`, which refuses while we are in it — a listener pressing play
    // during a wait must not start something, but the wait ending must.
    this.setStatus("loading");
    if (this.intendPlaying && chapterSleepDue(this.sleepTimer, page.chapterOrd)) {
      // The chapter the timer was set for ended while we waited. Do not start the next one.
      this.intendPlaying = false;
      this.sleepTimer = SLEEP_OFF;
      this.emit({ type: "sleep", state: this.sleepTimer, fired: true });
    }
    if (this.intendPlaying) await this.play();
    else this.setStatus("paused");
    void this.prepareAhead(this.gen);
  }

  private endOfBook(): void {
    this.intendPlaying = false;
    this.setStatus("paused");
    this.stopFrames();
    this.media.setPlaybackState("paused");
    this.emit({ type: "end-of-book" });
  }

  // ─── prefetch ──────────────────────────────────────────────────────────────

  /**
   * §4.9's belt and braces: prime **N+1 and N+2** as blobs before the screen can lock, so a hidden
   * tab needs no network for the next three minutes.
   *
   * N+1 also gets assigned to the idle deck, which is what makes the boundary a swap rather than a
   * load. N+2 is held in the `PageWindow` only — there are two elements, and a third would have to be
   * created after the unlock gesture, which is the one thing it may not be.
   */
  private async prepareAhead(gen: number): Promise<void> {
    const seq = ++this.aheadSeq;
    this.aheadInFlight = true;
    this.aheadAskedAt = this.host.now();
    try {
      await this.fillAhead(gen);
    } finally {
      if (seq === this.aheadSeq) this.aheadInFlight = false;
    }
  }

  /**
   * `timeupdate`'s half of the prefetch: N+1 was not ready when this page started, so ask again —
   * every `AHEAD_RECHECK_MS` of playback, until it is primed on the idle deck. See the constant.
   */
  private recheckAhead(): void {
    if (this.aheadSettled || this.aheadInFlight || this.source === null) return;
    if (this.host.now() - this.aheadAskedAt < AHEAD_RECHECK_MS) return;
    void this.prepareAhead(this.gen);
  }

  private async fillAhead(gen: number): Promise<void> {
    const source = this.source;
    const cur = this.decks[this.at];
    if (source === null || cur.page === null || this.destroyed) return;

    const currentId = cur.page.audio.pageId;
    const ids = source.pagesAfter(currentId, PREFETCH_AHEAD);
    /**
     * ⚠️ **The draining deck's page is kept too.** `pageWindow.ts` states the invariant this protects
     * verbatim: *"a URL is revoked only when no `<audio>` element still points at it."* At a boundary
     * this runs with the NEW current page while the other deck is `draining: true` with its `src`
     * still on page N's object URL, so page N falls out of `primeTargets` and its blob is revoked
     * underneath an element that is playing it. It survives today only because WebKit holds a blob for
     * an in-flight load — and `open()`'s fast path will happily swap back onto that deck, where a
     * `load()` on a revoked URL is `MEDIA_ERR_SRC_NOT_SUPPORTED` several seconds later, at a page
     * boundary, which is the failure that comment predicts.
     */
    const drainingId = this.decks[1 - this.at].draining ? (this.decks[1 - this.at].page?.audio.pageId ?? null) : null;
    const keep = (): string[] => {
      const held = primeTargets(currentId, ids);
      return drainingId !== null && !held.includes(drainingId) ? [...held, drainingId] : held;
    };
    this.window.keep(keep());
    if (ids.length === 0) {
      this.clearDeck(this.decks[1 - this.at]);
      if (gen === this.gen) this.aheadSettled = true;
      return;
    }

    /**
     * "Have we given the idle deck the page we will swap to?" — and NOT `i === 0`.
     *
     * A page with nothing to say is stepped over (see below), so the page that actually comes next
     * may be the second or third id in the list. Keyed off the index, the deck would be left empty
     * and every plate in the book would cost a stall at its boundary rather than being skipped.
     */
    let nextDeckArmed = false;
    let blocked = false;
    /** N+1 is blocked by something re-asking cannot fix — see `worthReasking`. */
    let hopeless = false;
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i] as string;
      const res = await this.resolvePage(id);
      if (gen !== this.gen || this.destroyed) return;
      // Past a page that is not ready, the rest are ASKED and nothing more: asking is what puts N+2
      // in the queue behind N+1, so the GPU is already on it when N+1 lands. Priming or arming a page
      // beyond a gap would be arming the wrong page.
      if (blocked) continue;
      if (!res.ok) {
        // A page with nothing to say is stepped over, not waited on. The reader's `pagesAfter` learns
        // it too and excludes it from here on, so this branch is the FIRST look at a plate and the
        // only one that costs anything.
        if (res.silent === true) continue;
        // Not an error here: the page is queued or building. `recheckAhead` asks again while this
        // page plays, and the boundary handles whatever is still missing with `waitForNext`.
        if (!nextDeckArmed) this.clearDeck(this.decks[1 - this.at]);
        blocked = true;
        hopeless = !worthReasking(res);
        continue;
      }

      const primed = await this.window.prime(res.page);
      if (gen !== this.gen || this.destroyed) return;

      if (!nextDeckArmed) {
        nextDeckArmed = true;
        const idle = this.decks[1 - this.at];
        await this.whenFree(idle);
        if (gen !== this.gen || this.destroyed) return;
        this.assign(idle, res.page, chooseSource(res.page, primed));
      }
      this.window.keep(keep());
    }
    if (gen === this.gen && (nextDeckArmed || !blocked || hopeless)) this.aheadSettled = true;
  }

  /**
   * `source.pageAudio`, with its rejection turned into an answer.
   *
   * ⚠️ Both callers are started with `void`, so a rejected promise here is an unhandled rejection —
   * and, worse, it abandons the poll ladder: the book stops at a page boundary with the status stuck
   * on `waiting` and a banner that keeps promising the next page forever. A dropped request while
   * polling is `missing`, not `failed`, so the ladder retries it; only the server saying `failed`
   * ends the wait.
   */
  private async resolvePage(pageId: string): Promise<PageResolution> {
    const source = this.source;
    if (source === null) return { ok: false, state: "missing", etaSec: null, message: "no source is wired" };
    try {
      return await source.pageAudio(pageId);
    } catch (err) {
      return { ok: false, state: "missing", etaSec: null, message: String(err) };
    }
  }

  /**
   * Wait for a deck to stop making sound before we take it.
   *
   * On the near-end path the deck we are about to reuse is still playing the last 40 ms of the page
   * the listener is hearing. Assigning a `src` to it stops it dead — clipping the final consonant of
   * every page, on exactly the boundaries where `ended` was unreliable. `DRAIN_MS` bounds the wait so
   * a deck that never fires `ended` cannot strand the prefetch either.
   */
  private whenFree(deck: Deck): Promise<void> {
    if (deck.el.paused || deck.el.ended || !deck.draining) return Promise.resolve();
    return new Promise<void>((resolve) => {
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        deck.el.removeEventListener("ended", finish);
        deck.draining = false;
        resolve();
      };
      deck.el.addEventListener("ended", finish);
      this.after(DRAIN_MS, finish);
    });
  }

  // ─── decks ─────────────────────────────────────────────────────────────────

  private assign(deck: Deck, page: EnginePage, src: { url: string; kind: SourceKind }): void {
    deck.page = page;
    deck.src = src.url;
    deck.kind = src.kind;
    deck.draining = false;
    deck.el.src = src.url;
    deck.el.load();
    this.applyRate(deck.el);
  }

  /**
   * ⚠️ **`removeAttribute("src")`, never `src = ""`.**
   *
   * An empty `src` *attribute* is a relative URL, and WebKit resolves it against the document base
   * rather than treating the resource as absent — so `el.src = ""` makes the element fetch
   * `/b/<id>/read` and try to decode the SPA's HTML as media. This runs on the idle deck from
   * `prepareAhead` whenever N+1 is not ready yet, i.e. on a locked phone, mid-chapter, on cellular,
   * while the other deck is playing: a spurious page-sized GET, an element parked in
   * `MEDIA_ERR_SRC_NOT_SUPPORTED`, and a second element competing for the media pipeline while the
   * Now Playing session is live. `onElementError`'s `deck.src === ""` guard is what kept it silent.
   * Removing the attribute and calling `load()` is the only spec-defined way to detach a resource.
   */
  private clearDeck(deck: Deck): void {
    if (deck.page === null && deck.src === "") return;
    deck.page = null;
    deck.src = "";
    deck.kind = null;
    deck.draining = false;
    deck.el.removeAttribute("src");
    deck.el.load();
  }

  private stopDeck(deck: Deck): void {
    try {
      deck.el.pause();
    } catch {
      /* nothing to stop */
    }
    deck.draining = false;
  }

  /**
   * Everything that must happen exactly once when a page becomes the one being heard.
   *
   * ⚠️ **It bumps `gen`, and that is load-bearing.** A prefetch started for the *previous* current
   * page is still running, and its idea of "the idle deck" is one page stale. If it lands after this
   * page has started, it assigns the page that is **playing** onto the deck we advance into next —
   * and the book plays that page, then plays it again. `test/audio-engine.test.ts` reproduces it on
   * the last page of a book, where nothing later overwrites the stale assignment: without this line
   * the book's final page plays twice instead of ending. On a fast link the window is too small to
   * see, which is exactly what makes it a defect that only ever happens on somebody's phone.
   */
  private becomeCurrent(via: "open" | "advance" | "resume" | "artifact"): void {
    const page = this.currentPage;
    if (page === null) return;
    this.gen++;
    this.rearmAdvance();
    this.deferred = null;
    this.aheadSettled = false;
    // An end-of-chapter timer means the chapter being LISTENED to. A jump to another chapter moves
    // it there, or it would fire at the first boundary of the chapter the listener chose.
    if (via === "open" && this.sleepTimer.kind === "chapter") this.sleepTimer = armSleep("chapter", page.chapterOrd);
    this.media.setChapter(page.chapterTitle);
    this.syncPosition();
    this.emit({ type: "page", page, via });
  }

  private rearmAdvance(): void {
    this.advanceArmed = true;
  }

  // ─── the frame loop ────────────────────────────────────────────────────────

  /**
   * One rAF loop for the whole engine, and the read-along rides its tick rather than starting a
   * second one. Two loops on a phone is two wakeups per frame for one clock.
   *
   * ⚠️ **Always rescheduled, and every listener is called inside a `try`.** §5.5's lesson, one level
   * down: an exception inside a rAF callback is not caught anywhere, the loop is never rescheduled,
   * and the page plays with no highlight and no turn — recovering at the next page, so it reads as
   * "flaky" rather than as a crash. The read-along is a subscriber, so its bug must not be ours.
   */
  private startFrames(): void {
    if (this.rafHandle !== 0 || this.destroyed) return;
    const frame = (): void => {
      this.rafHandle = 0;
      if (this.status !== "playing" || this.destroyed) return;
      const deck = this.decks[this.at];
      if (this.nearEnd(deck)) this.advance();
      const page = this.currentPage;
      if (page !== null) this.emit({ type: "tick", masterMs: this.masterMs(), page });
      /**
       * ⚠️ `this.rafHandle === 0` is the whole of the re-entrancy guard, and it belongs here as well
       * as in `startFrames`.
       *
       * The line above cleared `rafHandle` on entry. If `nearEnd` fired, `advance()` → `startFrames()`
       * saw a zero handle, decided no loop was running, and scheduled one — and then this line
       * scheduled a *second* and overwrote the handle, so `stopFrames()` could only ever cancel one of
       * them. That path is not exotic: §5.7 says Safari does not reliably fire `ended` for short
       * blob-backed media, which makes the rAF near-end check the PRIMARY boundary trigger on the only
       * device that matters. Four boundaries, up to sixteen live loops, each calling `masterMs()` and
       * emitting a tick to the read-along sixty times a second. Nothing errors; the phone gets warm
       * and the battery is gone by chapter nine — the exact cost §5.7 chose `<audio>` over Web Audio
       * to avoid. `attachVisibilityResync` already carries this condition; this is the same one.
       */
      if (this.status === "playing" && !this.destroyed && this.rafHandle === 0) this.rafHandle = this.host.raf(frame);
    };
    this.rafHandle = this.host.raf(frame);
  }

  private stopFrames(): void {
    if (this.rafHandle === 0) return;
    this.host.cancelRaf(this.rafHandle);
    this.rafHandle = 0;
  }

  // ─── element events ────────────────────────────────────────────────────────

  private onLoadedMetadata(deck: Deck): void {
    if (this.suspendEvents) return;
    this.applyRate(deck.el);
    if (deck !== this.decks[this.at]) return;
    const held = this.pendingSeekMs;
    if (held !== null) {
      this.pendingSeekMs = null;
      this.seekMasterMs(held);
    }
    this.syncPosition();
  }

  private onElementPlay(deck: Deck): void {
    if (this.suspendEvents || deck !== this.decks[this.at]) return;
    // The OS started us: a lock-screen button, a car stereo, an AirPods squeeze that Safari handled
    // itself rather than routing through our action handler.
    this.intendPlaying = true;
    if (this.status !== "playing") {
      this.setStatus("playing");
      this.media.setPlaybackState("playing");
    }
    this.startFrames();
  }

  private onElementPause(deck: Deck): void {
    if (this.suspendEvents || deck !== this.decks[this.at]) return;
    // An `ended` element is "paused" too; that is the boundary, not a pause, and calling it one would
    // put the lock screen into a paused state for the half second the swap takes.
    if (deck.el.ended) return;
    if (this.status === "playing") {
      this.setStatus("paused");
      this.media.setPlaybackState("paused");
      this.stopFrames();
      // The OS-initiated pause — an AirPods squeeze, a car stereo, a phone call — has to re-report
      // where we are, exactly as `pause()` does. iOS freezes its interpolated scrubber at a pause, so
      // without this the last authoritative `setPositionState` can be a whole page old and the lock
      // screen and the app disagree after every interruption.
      this.syncPosition();
      this.sleepLastAt = null;
      this.emit({ type: "listen-point", reason: "pause" });
    }
  }

  private onElementError(deck: Deck): void {
    if (this.suspendEvents) return;
    /**
     * ⚠️ **An empty `src` is OUR OWN teardown, never a decode failure.**
     *
     * `clearDeck` sets `el.src = ""`, and the browser answers with a real `error` event —
     * `MEDIA_ELEMENT_ERROR: Empty src attribute`, with `error.code` 4, indistinguishable at this
     * point from an artifact that would not decode. Reported as a failure it becomes *"This page's
     * audio couldn't be played"* on screen, for a deck we deliberately emptied a moment earlier.
     *
     * `deck.src` is the engine's own record of what it assigned, so this is asking "did we clear
     * this?" rather than trusting the element. Found by the first end-to-end browser run, where it
     * turned a recoverable unlock race into a terminal error banner.
     */
    if (deck.src === "") return;
    if (deck !== this.decks[this.at]) {
      // A prefetch that failed to decode. Drop it and let the boundary re-resolve rather than
      // swapping onto a deck that cannot play — a swap onto a broken element is silence with no event.
      this.clearDeck(deck);
      return;
    }
    this.fail("This page's audio couldn't be played. The book still reads.", `media error on deck ${deck.name}`);
  }

  // ─── LAW 11 ────────────────────────────────────────────────────────────────

  /**
   * The secondary liveness signal (§4.9, LAW 11). Sent from the swap, never from a timer.
   *
   * `keepalive: true` so it survives the tab being frozen a moment later; failures are swallowed
   * entirely, because a beacon that throws into a page swap is a beacon that costs a page turn — and
   * a beacon somebody deletes.
   */
  private async beat(playing: boolean): Promise<void> {
    const page = this.currentPage;
    if (page === null) return;
    const now = this.host.now();
    if (now - this.lastBeatAt < BEAT_MIN_MS) return;
    this.lastBeatAt = now;

    try {
      const res = await this.host.fetch("/api/v1/sessions/beat", {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          pageId: page.audio.pageId,
          // LAW 1. A page id is only meaningful inside its edition, and the route refuses without it.
          editionId: page.audio.editionId,
          playing,
          sessionId: this.sessionId,
        }),
      });
      if (!res.ok) return;
      const body = (await res.json()) as { sessionId?: unknown };
      if (typeof body.sessionId === "string" && body.sessionId !== "") this.sessionId = body.sessionId;
    } catch {
      /* the proxy is the primary signal; this one is allowed to be lossy */
    }
  }

  // ─── the lock screen's scrubber ────────────────────────────────────────────

  /**
   * §5.7: chapter-scoped, and **not** every frame.
   *
   * Called on swap, seek, rate change and play/pause. A per-frame `setPositionState` is 60 IPC calls
   * a second to the OS for a number that moves by itself — the platform interpolates from the rate,
   * which is exactly why the rate is part of the state.
   */
  private syncPosition(): void {
    const page = this.currentPage;
    if (page === null) return;
    const t = this.decks[this.at].el.currentTime;
    this.media.setPosition({
      duration: page.chapterDurationSec,
      playbackRate: this.rate,
      position: page.chapterOffsetSec + (Number.isFinite(t) ? t : 0),
    });
  }

  // ─── plumbing ──────────────────────────────────────────────────────────────

  private setStatus(status: EngineStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.emit({ type: "status", status });
  }

  private fail(message: string, detail: string): void {
    this.intendPlaying = false;
    this.stopFrames();
    this.setStatus("error");
    this.media.setPlaybackState("paused");
    this.emit({ type: "error", message, detail });
  }

  private emit(ev: EngineEvent): void {
    for (const fn of [...this.listeners]) {
      try {
        fn(ev);
      } catch {
        /* a subscriber's bug must not cost the listener the rest of the book — see startFrames() */
      }
    }
  }

  private after(ms: number, fn: () => void): void {
    const token = this.host.setTimer(() => {
      this.timers.delete(token);
      fn();
    }, ms);
    this.timers.add(token);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise<void>((resolve) => this.after(ms, resolve));
  }

  /**
   * Stop, forget the book, and hand the lock screen back — **without killing the engine.**
   *
   * ⚠️ This is what a reader calls on unmount, and `destroy()` is not. The engine is constructed once
   * at module load and lives for the life of the document (see the constructor: both elements must
   * exist before any gesture), so there is exactly one of it and every book shares it. Nothing called
   * anything on unmount, and the consequence was not subtle: start book A, tap back to the shelf,
   * open book B — book A never stopped. `setSource` is replaced with B's closures, but `decks[at].page`
   * is still A's page, so the play button takes the `currentPage !== null` branch and resumes **book
   * A** with book B on screen; the Now Playing card still says A's chapter and its handlers still
   * close over A; `prepareAhead` keeps fetching A's pages over cellular. On iOS the card outlives the
   * reader indefinitely, because `media.release()` was never reached.
   *
   * `destroy()` sets `destroyed = true` permanently, which on a document-lifetime singleton means the
   * SECOND visit to any reader gets an engine that returns `false` from everything and makes no sound.
   * That is why these are two methods and why this one is the one the reader wants.
   */
  close(): void {
    if (this.destroyed) return;
    this.intendPlaying = false;
    this.gen++;
    this.stopFrames();
    for (const token of this.timers) this.host.clearTimer(token);
    this.timers.clear();
    for (const deck of this.decks) {
      this.stopDeck(deck);
      this.clearDeck(deck);
    }
    this.window.clear();
    this.deferred = null;
    this.pendingSeekMs = null;
    this.sleepTimer = SLEEP_OFF;
    this.sleepStopSid = undefined;
    this.setVolume(1);
    this.sessionId = null;
    this.lastBeatAt = Number.NEGATIVE_INFINITY;
    this.nowPlaying = null;
    this.source = null;
    this.media.release();
    // `disable()`, not `destroy()`: the visibility listener is the wake lock's own re-request path
    // (§5.7 — "re-requested on visibilitychange → visible") and it must survive to the next book.
    void this.wakeLock.disable();
    this.setStatus("idle");
    // `unlocked` is deliberately NOT reset: the grant belongs to the elements and to the document,
    // not to the book, and re-locking would cost the listener a gesture they already gave.
  }

  /** Give everything back, permanently. Tests call this; a reader wants `close()`. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.gen++;
    this.stopFrames();
    for (const token of this.timers) this.host.clearTimer(token);
    this.timers.clear();
    for (const deck of this.decks) {
      this.stopDeck(deck);
      this.clearDeck(deck);
    }
    this.detachVisibility?.();
    this.detachVisibility = null;
    this.window.clear();
    this.media.release();
    try {
      this.tabs?.close();
    } catch {
      /* already closed */
    }
    void this.wakeLock.destroy();
    this.listeners.clear();
  }

  /** Diagnostics for the reader's debug panel and for tests. Never a control path. */
  inspect(): {
    status: EngineStatus;
    deck: "a" | "b";
    current: string | null;
    next: string | null;
    kinds: { current: SourceKind | null; next: SourceKind | null };
    primed: string[];
    unlocked: boolean;
    rate: number;
  } {
    const cur = this.decks[this.at];
    const other = this.decks[1 - this.at];
    return {
      status: this.status,
      deck: cur.name,
      current: cur.page?.audio.pageId ?? null,
      next: other.page?.audio.pageId ?? null,
      kinds: { current: cur.kind, next: other.kind },
      primed: this.window.held().map((p) => p.pageId),
      unlocked: this.unlocked,
      rate: this.rate,
    };
  }
}

/**
 * Is a not-ready N+1 worth asking about again in `AHEAD_RECHECK_MS`?
 *
 * ⚠️ **`failed` is not.** `ensure` dedupes only against queued/running jobs, so re-asking a failed page
 * enqueues a NEW narration every 12 s for as long as the page before it plays — a GPU loop and a token
 * drain the server's governor warns about by name. A failed page waits for a human Retry. Likewise a
 * `quota` refusal: asking again inside the hour only spends the answer. Queued, building and missing
 * are the states that change on their own.
 */
export function worthReasking(res: { state: PageState; code?: string | null }): boolean {
  if (res.code === "quota") return false;
  return res.state === "queued" || res.state === "building" || res.state === "missing";
}

function makeDeck(el: AudioLike, name: "a" | "b"): Deck {
  return { el, name, page: null, src: "", kind: null, draining: false, unlocked: false };
}

/** The part of an `EventTarget` the unlock gesture uses. `document` satisfies it. */
export interface GestureTarget {
  addEventListener(type: "pointerup", handler: () => void, capture?: boolean): void;
  removeEventListener(type: "pointerup", handler: () => void, capture?: boolean): void;
}

// ─── the browser ─────────────────────────────────────────────────────────────

function browserDocument(): VisibilityHost | null {
  return typeof document === "undefined" ? null : (document as unknown as VisibilityHost);
}

/**
 * The real browser, or `null` under `node --test` and any other DOM-less host.
 *
 * ⚠️ **The elements are appended to `document.body`, and they are still outside React.** The original
 * left them detached with the claim that "a detached `<audio>` drives Media Session and routes to
 * AirPlay exactly like an attached one". The first half is true and observed; the second half is a
 * bet about WebKit's route picker that nobody on this project can test — there is no monitor and no
 * Apple TV here. Appending costs nothing, keeps the property the comment was actually protecting
 * (they are in no React subtree, so no unmount can take away the thing making sound), and removes
 * the question. **Not `display: none`** — WebKit treats display:none media elements differently, and
 * a zero-size audio element is already invisible.
 */
export function browserEngineHost(): EngineHost | null {
  if (typeof document === "undefined" || typeof window === "undefined") return null;
  return {
    createAudio: () => {
      const el = document.createElement("audio");
      el.preload = "auto";
      // iOS only honours it as an attribute, and without it a media element can be pulled into the
      // fullscreen player on some iPhones — for an audiobook, over the page you are reading.
      el.setAttribute("playsinline", "");
      el.setAttribute("data-ab-audio", "");
      document.body.appendChild(el);
      return el;
    },
    tabChannel: () =>
      typeof BroadcastChannel === "undefined" ? null : (new BroadcastChannel("ab-audio") as unknown as TabChannel),
    /**
     * ⚠️ **The offline half of §5.7, and the reason LAW 14 costs nothing.**
     *
     * The service worker must not claim `/api/v1/audio/` — a cache-first worker answering a plain
     * `200` to an `<audio>` element's `Range: bytes=0-1` is how Safari decides a resource is not
     * seekable — so a downloaded book's bytes are held in a Cache the *page* owns and read back
     * here, where the result becomes a Blob and an `objectURL`. That is the path §5.7 already
     * specifies for prefetched pages and offline books; nothing about it involves Range.
     *
     * Network first, always: a book downloaded three weeks ago must not shadow a page that has been
     * re-narrated since. The URL carries `?v=<artifactSha>`, so a re-narrated page is a different
     * key and a stale hit is structurally impossible — but a *deleted* page would still be here, and
     * asking the server first is what keeps this a fallback rather than a second source of truth.
     */
    fetch: async (input, init) => {
      try {
        return await fetch(input, init);
      } catch (err) {
        // A *transport* failure only. A 404 or a 403 is an answer and is handed straight back to
        // `PageWindow`, which records it as a prime failure — serving a cached artifact over a
        // server that has just refused the page would be a capability that outlives its grant.
        const cached = await cachedAudioResponse(input);
        if (cached) return cached;
        throw err;
      }
    },
    createObjectURL: (blob) => URL.createObjectURL(blob as Blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
    now: () => Date.now(),
    raf: (cb) => window.requestAnimationFrame(cb),
    cancelRaf: (h) => window.cancelAnimationFrame(h),
    setTimer: (fn, ms) => window.setTimeout(fn, ms),
    clearTimer: (token) => window.clearTimeout(token as number),
  };
}

/**
 * **The engine, created once at module load** — which is the whole point (see the constructor).
 *
 * `null` under Node, where there is no `document`: the alternative is a stub that pretends to be an
 * audio element, and a stub that pretends is how a test suite stays green over a player that makes no
 * sound. Tests construct their own `new AudioEngine(fakeHost)` and say so.
 */
export const audioEngine: AudioEngine | null = (() => {
  const host = browserEngineHost();
  if (host === null) return null;
  const engine = new AudioEngine(host);
  /**
   * §6.6's AirPlay path, attached here and not in the reader — because **the route belongs to the
   * elements, and this is where the elements are made.**
   *
   * A reader that mounted it would attach on `/b/:id/read` and lose it on every unmount, and the two
   * decks it has to listen on are private to this module. `installAirPlay` returns `null` with no DOM
   * (so `node --test` and every `new AudioEngine(fakeHost)` are untouched) and does nothing at all
   * until WebKit reports a route — on Chrome, Firefox and Node it is two `addEventListener` calls for
   * events that never fire.
   *
   * What it buys: when an iPhone hands off to an Apple TV the receiver re-fetches **with no access to
   * Safari's cookie jar**, so without this a private or unlisted book 404s per page and AirPlay
   * silently does nothing. `client/src/audio/airplay.ts` carries the whole argument.
   */
  void installAirPlay(engine);
  return engine;
})();

/** For call sites that legitimately cannot run without it. Throws with a sentence, never `undefined`. */
export function requireAudioEngine(): AudioEngine {
  if (audioEngine === null) {
    throw new Error("There is no audio engine here: AudioEngine.ts was loaded without a DOM.");
  }
  return audioEngine;
}
